// FastTrackRiskEvaluation.js - AI-powered theme-based risk evaluation for co-pilot

import React, { useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import {
  Box,
  Card,
  Text,
  Button,
  Badge,
  Stack,
  Container,
  Title,
  Group,
  Alert,
  Checkbox,
  Select,
  Textarea,
  Grid,
  ActionIcon,
  Table,
  Tooltip,
} from '@mantine/core';
import {
  IconSparkles,
  IconCheck,
  IconAlertCircle,
  IconChevronDown,
  IconChevronUp,
  IconRefresh,
  IconExclamationMark,
  IconDeviceFloppy,
  } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';

import { updateRiskPriority, fetchRiskEvaluations } from '../redux/actions/riskActions';
import AutosaveIndicator from '../AutoSaveIndicator';
import useSaveOperation from '../useSaveOperation';
import AIGeneratingIcon from '../components/AIGeneratingIcon';
import { Navigation } from '../components/NavigationButtons';

const PRIORITY_LEVELS = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'significant', label: 'Significant' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'low', label: 'Low' },
  { value: 'negligible', label: 'Negligible' },
];

// Risk priority colors from existing palette
const riskPriorityColors = {
  Critical: '#1A1A1A',    // Black
  High: '#993333',        // Red
  Significant: '#B94D4D', // Amber
  Moderate: '#D4AA7D',    // Light Orange
  Low: '#B8C2B7',         // Blue
  Negligible: '#D8D8D8',  // Green
  None: '#EBEBEB',        // Light Grey
};

// Get risk color for badges
const getRiskColor = (level) => {
  const normalizedLevel = level?.charAt(0).toUpperCase() + level?.slice(1).toLowerCase();
  return riskPriorityColors[normalizedLevel] || riskPriorityColors.None;
};


export default function FastTrackRiskEvaluation({
  modelId,
  assessmentUuid,
  riskArea,
  source,
  onNext,
  onBack,
}) {
  const dispatch = useDispatch();
  const { isSaving, isSaved, handleSaveOperation } = useSaveOperation();
  const themes = useSelector((state) => state.themes);
  
  // Get evaluation criteria from configs state
  const evaluationCriteria = useSelector((state) => 
    state.config?.configs?.riskEvaluationForm || []
  );

  // Helper functions to get options from evaluation criteria
  const getOptionsForField = useCallback((attribute, fieldName) => {
    const criterion = evaluationCriteria.find(c => c.attribute === attribute);
    if (!criterion) return [];
    
    // Check baseEvaluation first
    if (criterion.baseEvaluation?.name === fieldName) {
      return criterion.baseEvaluation.options || [];
    }
    
    // Check deepDive fields
    const deepDiveField = criterion.deepDive?.find(field => field.name === fieldName);
    return deepDiveField?.options || [];
  }, [evaluationCriteria]);

  // Specific helper functions for commonly used fields
  const getRiskImpactOptions = useCallback(() => getOptionsForField('riskImpact', 'overallImpact'), [getOptionsForField]);
  const getLikelihoodOptions = useCallback(() => getOptionsForField('likelihood', 'probability'), [getOptionsForField]);
  const getIntensityOptions = useCallback(() => getOptionsForField('riskImpact', 'intensity'), [getOptionsForField]);
  const getDurationOptions = useCallback(() => getOptionsForField('riskImpact', 'duration'), [getOptionsForField]);
  const getFrequencyOptions = useCallback(() => getOptionsForField('likelihood', 'frequencyOfOccurrence'), [getOptionsForField]);
  

  // Get risk and causes data
  const savedRisk = useSelector((state) => {
    const risks =
      state.risks.models[modelId]?.riskFormData[riskArea]?.sectionRisks?.[source.name] || [];
    return risks.find((r) => r.riskId) || risks[0];
  });

  // Get causes data using RiskSummaryReport approach
  const sourceCauses = useSelector((state) => {
    const riskFormData = state.risks.models[modelId]?.riskFormData?.[riskArea];
    
    // First try: from riskSources array
    const fromRiskSources = riskFormData?.riskSources?.find(src => src.name === source?.name)?.causes;
    if (fromRiskSources && Object.keys(fromRiskSources).length > 0) {
      console.log('[FastTrack] Found causes from riskSources:', Object.keys(fromRiskSources).length, 'causes');
      return fromRiskSources;
    }
    
    // Second try: from causesData if it exists
    const fromCausesData = riskFormData?.causesData?.[source?.name];
    if (fromCausesData && Object.keys(fromCausesData).length > 0) {
      console.log('[FastTrack] Found causes from causesData:', Object.keys(fromCausesData).length, 'causes');
      return fromCausesData;
    }
    
    console.log('[FastTrack] No causes found for source:', source?.name);
    return {};
  });
  
  // Convert causes object to array format for the API
  const savedCauses = useMemo(() => {
    return Object.entries(sourceCauses).map(([name, data]) => ({
      name,
      description: data.description || data.rationale || '',
      ...data
    }));
  }, [sourceCauses]);

  // Get unified model narrative for impact assessment
  const contextData = useSelector((state) => state.context.data[modelId] || {});
  const modelNarrative = useMemo(() => {
    return contextData?.unifiedNarrative?.narrative || '';
  }, [contextData]);

  // State management
  const [isEvaluating, setIsEvaluating] = useState(false);
  const [fastTrackResult, setFastTrackResult] = useState(null);
  const [themeSelections, setThemeSelections] = useState({});
  const [themeEvaluations, setThemeEvaluations] = useState({});
  const [localPriority, setLocalPriority] = useState('');
  const [localRationale, setLocalRationale] = useState('');
  
  // Matrix interface state
  const [expandedCells, setExpandedCells] = useState({}); // For individual cell expansion (impact/likelihood)
  const [hasChanges, setHasChanges] = useState(false); // Track if evaluation has been modified
  const [isGeneratingTheme, setIsGeneratingTheme] = useState({}); // Track themes being AI-evaluated
  
  // Check if evaluation is already saved
  const isEvaluationSaved = useMemo(() => !!savedRisk?.priority, [savedRisk?.priority]);
  
  // Check if we have actual evaluation data (not just priority)
  const hasEvaluationData = useMemo(() => {
    return savedRisk?.evaluationData && Object.keys(savedRisk.evaluationData).length > 0;
  }, [savedRisk?.evaluationData]);
  
  // Fetch evaluation data on component mount
  React.useEffect(() => {
    const fetchData = async () => {
      await dispatch(fetchRiskEvaluations(modelId, assessmentUuid, riskArea, source.name));
    };
    fetchData();
  }, [dispatch, modelId, assessmentUuid, riskArea, source.name]);
  
  
  // Initialize state from saved evaluation data synchronously
  React.useLayoutEffect(() => {
    if (hasEvaluationData && savedRisk && !Object.keys(themeSelections).length) {
      console.log('[FastTrack] Hydrating from saved evaluations');
      
      // Set theme selections and evaluations for editing
      const selections = {};
      const evaluations = {};
      Object.entries(savedRisk.evaluationData).forEach(([themeId, data]) => {
        selections[themeId] = true;
        evaluations[themeId] = {
          impact: {
            overallImpact: data.riskImpact?.overallImpact || '',
            intensity: data.riskImpact?.intensity || '',
            duration: data.riskImpact?.duration || '',
            probability: data.likelihood?.probability || '',
            frequencyOfOccurrence: data.likelihood?.frequencyOfOccurrence || ''
          }
        };
      });
      
      setThemeSelections(selections);
      setThemeEvaluations(evaluations);
      setLocalPriority(savedRisk.priority);
      setLocalRationale(savedRisk.rationale || '');
      
      console.log('[FastTrack] Initialized with saved evaluation data:', {
        themes: Object.keys(savedRisk.evaluationData).length,
        priority: savedRisk.priority,
      });
    }
  }, [hasEvaluationData, savedRisk, themeSelections]);

  // Debounced auto-save for individual field changes
  const debouncedSaveEvaluation = useMemo(
    () =>
      debounce(async (themeId, attribute, property, value) => {
        if (!savedRisk?.riskId || !isEvaluationSaved) return; // Only auto-save if already saved once
        
        try {
          const response = await fetch('/.netlify/functions/saveRiskEvaluation', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              modelUuid: modelId,
              assessmentUuid,
              riskName: savedRisk.riskName,
              riskId: savedRisk.riskId,
              themeId: parseInt(themeId),
              riskArea,
              priority: localPriority,
              rationale: localRationale,
              data: themeEvaluations[themeId] || {},
            }),
          });

          if (!response.ok) {
            throw new Error('Failed to auto-save evaluation');
          }

          console.log(`[FastTrack] Auto-saved ${attribute}.${property} = ${value} for theme ${themeId}`);
        } catch (error) {
          console.error('[FastTrack] Auto-save error:', error);
        }
      }, 800),
    [modelId, assessmentUuid, savedRisk, localPriority, localRationale, themeEvaluations, isEvaluationSaved, riskArea]
  );

  // Handle FastTrack evaluation
  const handleFastTrackEvaluation = async () => {
    if (!savedRisk) {
      notifications.show({
        title: 'Error',
        message: 'No risk data available for evaluation',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
      return;
    }

    // RESET existing evaluation data for fresh analysis
    setFastTrackResult(null);
    setThemeSelections({});
    setThemeEvaluations({});
    setLocalPriority('');
    setLocalRationale('');

    setIsEvaluating(true);
    
    console.log('[FastTrack] Context data:', contextData);
    console.log('[FastTrack] Model narrative:', modelNarrative);
    console.log('[FastTrack] Causes:', savedCauses);
    
    try {
      const response = await fetch('/.netlify/functions/fastTrackRiskEvaluation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          risk: savedRisk,
          causes: savedCauses,
          contextData: contextData,
          modelUuid: modelId,
          assessmentUuid,
          riskArea,
          sourceName: source.name,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'FastTrack evaluation failed');
      }

      const data = await response.json();
      const evaluation = data.fastTrackEvaluation;
      
      setFastTrackResult(evaluation.fastTrackData);
      
      // Initialize theme selections and evaluations
      const selections = {};
      const evaluations = {};
      
      evaluation.fastTrackData.selectedThemes.forEach(theme => {
        selections[theme.themeId] = true;
        // Ensure AI values are properly synced to evaluation structure
        const themeEval = theme.evaluation || { impact: {} };
        if (!themeEval.impact) themeEval.impact = {};
        
        // Sync high-level assessment values to detailed evaluation
        if (theme.highLevelAssessment) {
          themeEval.impact.overallImpact = theme.highLevelAssessment.overallImpact;
          themeEval.impact.probability = theme.highLevelAssessment.probability;
        }
        
        evaluations[theme.themeId] = themeEval;
      });
      
      evaluation.fastTrackData.unselectedThemes?.forEach(theme => {
        selections[theme.themeId] = false;
      });
      
      setThemeSelections(selections);
      setThemeEvaluations(evaluations);
      setLocalPriority(evaluation.priority.toLowerCase());
      setLocalRationale(evaluation.rationale);

      notifications.show({
        title: 'FastTrack Complete',
        message: `Evaluated ${evaluation.fastTrackData.selectedThemes.length} themes${evaluation.confidence ? ` with ${(evaluation.confidence * 100).toFixed(0)}% confidence` : ''}`,
        color: 'green',
        icon: <IconSparkles size={16} />,
      });

    } catch (error) {
      console.error('FastTrack evaluation error:', error);
      notifications.show({
        title: 'FastTrack Error',
        message: error.message || 'Failed to evaluate risk',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsEvaluating(false);
    }
  };

  // Toggle theme selection
  const handleThemeToggle = (themeId, isSelected) => {
    setThemeSelections(prev => ({
      ...prev,
      [themeId]: isSelected
    }));

    if (isSelected && !themeEvaluations[themeId]) {
      // Initialize with default evaluation (impact-focused structure)
      const defaultEvaluation = {
        impact: {
          overallImpact: "Moderate",
          intensity: "", 
          duration: "",
          probability: "Possible",
          frequencyOfOccurrence: ""
        }
      };
      
      setThemeEvaluations(prev => ({
        ...prev,
        [themeId]: defaultEvaluation
      }));
    }
  };

  // Update theme evaluation with auto-save and change tracking
  const handleEvaluationChange = (themeId, attribute, property, value) => {
    // Update local state immediately
    setThemeEvaluations(prev => ({
      ...prev,
      [themeId]: {
        ...prev[themeId],
        [attribute]: {
          ...prev[themeId]?.[attribute],
          [property]: value
        }
      }
    }));

    // Mark as changed for Update Priority button
    setHasChanges(true);

    // Trigger auto-save if evaluation is already saved
    if (isEvaluationSaved) {
      debouncedSaveEvaluation(themeId, attribute, property, value);
    }
  };

  // Handle cell expansion (impact/likelihood cells in matrix)
  const toggleCellExpansion = (themeId, cellType) => {
    const cellKey = `${themeId}-${cellType}`;
    setExpandedCells(prev => ({
      ...prev,
      [cellKey]: !prev[cellKey]
    }));
  };

  // Generate AI evaluation for a newly selected theme
  const handleGenerateThemeEvaluation = async (themeId) => {
    if (!savedRisk || isGeneratingTheme[themeId]) return;

    setIsGeneratingTheme(prev => ({ ...prev, [themeId]: true }));

    try {
      const theme = themes.find(t => t.theme_id === parseInt(themeId));
      if (!theme) throw new Error('Theme not found');

      const response = await fetch('/.netlify/functions/fastTrackRiskEvaluation', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          risk: savedRisk,
          causes: savedCauses,
          contextData: contextData,
          modelUuid: modelId,
          assessmentUuid,
          riskArea,
          sourceName: source.name,
          targetThemes: [themeId], // Only evaluate this specific theme
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate theme evaluation');
      }

      const data = await response.json();
      const evaluation = data.fastTrackEvaluation;

      // Update just this theme's evaluation
      if (evaluation.fastTrackData.selectedThemes.length > 0) {
        const themeEval = evaluation.fastTrackData.selectedThemes[0];
        
        setThemeEvaluations(prev => ({
          ...prev,
          [themeId]: {
            impact: {
              overallImpact: themeEval.highLevelAssessment?.overallImpact || '',
              intensity: themeEval.evaluation?.impact?.intensity || '',
              duration: themeEval.evaluation?.impact?.duration || '',
              probability: themeEval.highLevelAssessment?.probability || '',
              frequencyOfOccurrence: themeEval.evaluation?.impact?.frequencyOfOccurrence || ''
            },
            reasoning: themeEval.evaluation?.reasoning || themeEval.reason || `Evaluated ${theme.name} risk impact and likelihood`
          }
        }));

        setHasChanges(true);
        
        notifications.show({
          title: 'Theme Evaluated',
          message: `AI evaluation completed for ${theme.name}`,
          color: 'green',
          icon: <IconCheck size={16} />,
        });
      }
    } catch (error) {
      console.error('Theme evaluation error:', error);
      notifications.show({
        title: 'Evaluation Failed',
        message: error.message || 'Failed to generate evaluation for theme',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    } finally {
      setIsGeneratingTheme(prev => ({ ...prev, [themeId]: false }));
    }
  };

  // Save FastTrack results
  const handleSave = async () => {
    await handleSaveOperation(async () => {
      // Build evaluation data for selected themes
      const selectedThemeData = [];
      Object.entries(themeSelections).forEach(([themeId, isSelected]) => {
        if (isSelected && themeEvaluations[themeId]) {
          selectedThemeData.push({
            themeId: parseInt(themeId),
            data: themeEvaluations[themeId]
          });
        }
      });

      if (selectedThemeData.length === 0) {
        throw new Error('No themes selected for evaluation');
      }

      // Save each theme evaluation separately (backend expects one theme per call)
      const savePromises = selectedThemeData.map(({themeId, data}) => 
        fetch('/.netlify/functions/saveRiskEvaluation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            modelUuid: modelId,
            assessmentUuid,
            riskName: savedRisk.riskName,
            riskId: savedRisk.riskId,
            themeId: themeId,
            riskArea,
            priority: localPriority,
            rationale: localRationale,
            data: data.impact || {}, // Extract impact data for backend
          }),
        })
      );

      // Wait for all theme saves to complete
      const responses = await Promise.all(savePromises);
      
      // Check if any saves failed
      const failedSaves = responses.filter(response => !response.ok);
      if (failedSaves.length > 0) {
        throw new Error(`Failed to save ${failedSaves.length} theme evaluations`);
      }

      console.log(`[FastTrack] Successfully saved ${selectedThemeData.length} theme evaluations`);

      // Update Redux state with priority and rationale
      await dispatch(
        updateRiskPriority(
          modelId,
          savedRisk.riskId,
          riskArea,
          source.name,
          localPriority,
          assessmentUuid,
          localRationale
        )
      );

      // Refresh evaluation data from database to get complete evaluationData
      await dispatch(fetchRiskEvaluations(modelId, assessmentUuid, riskArea, source.name));
      
      // Reset change tracking
      setHasChanges(false);
    });

    notifications.show({
      title: 'Saved',
      message: 'Risk evaluation saved successfully!',
      color: 'green',
      icon: <IconCheck size={16} />,
    });
  };

  // Completion check
  const isComplete = useMemo(() => {
    const hasSelectedThemes = Object.values(themeSelections).some(selected => selected);
    return hasSelectedThemes && localPriority && localRationale.trim();
  }, [themeSelections, localPriority, localRationale]);

  if (!savedRisk) {
    return (
      <Container fluid px="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          title="Risk Not Found"
        >
          No risk data available for FastTrack evaluation.
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid px="xl" py="xl">
      {/* Header */}
      <Stack spacing="md" mb="xl">
        <Group position="apart" align="flex-start">
          <Title order={3} color="dark.7">
            Risk Evaluation
          </Title>
          <AutosaveIndicator isSaving={isSaving} isSaved={isSaved} />
        </Group>

        <Group spacing="md">
          <Text size="md" color="dimmed">
            Evaluating risk:
          </Text>
          <Badge variant="filled" size="lg" radius="sm" color="indigo">
            {savedRisk.riskName}
          </Badge>
        </Group>
      </Stack>

      {/* FastTrack Trigger - only show if no evaluation data exists */}
      {!fastTrackResult && !hasEvaluationData && (
        <Card withBorder radius="md" p="lg" mb="xl">
          <Stack spacing="lg">
            <Group>
              <IconSparkles size={24} color="indigo" />
              <Title order={4}>AI Risk Analysis</Title>
            </Group>
            <Text c="dimmed" align="left">
              Analyze "{savedRisk.riskName}" and its {savedCauses.length} contributing causes 
              to automatically select relevant impact areas and provide initial evaluations.
            </Text>
            <Group>
              <Button
                onClick={handleFastTrackEvaluation}
                disabled={isEvaluating}
                leftSection={<AIGeneratingIcon isGenerating={isEvaluating} size={16} color="white" />}
                size="md"
                color="violet"
                variant="filled"
              >
                {isEvaluating ? 'Analyzing Risk...' : 'Evaluate Risk'}
              </Button>
            </Group>
          </Stack>
        </Card>
      )}
      
      {/* Saved Evaluation Notice - only for priority without evaluation data */}
      {isEvaluationSaved && !hasEvaluationData && !fastTrackResult && (
        <Card withBorder radius="md" p="lg" mb="xl" style={{ borderColor: '#51cf66', backgroundColor: '#f3f9f3' }}>
          <Stack spacing="lg">
            <Group>
              <IconCheck size={24} color="green" />
              <Title order={4} color="green">Evaluation Previously Saved</Title>
            </Group>
            <Text color="dimmed">
              This risk has been evaluated and saved with priority: <strong>{savedRisk.priority}</strong>
            </Text>
            <Group>
              <Button
                onClick={handleFastTrackEvaluation}
                disabled={isEvaluating}
                leftSection={<AIGeneratingIcon isGenerating={isEvaluating} size={16} color="white" />}
                size="md"
                color="violet"
                variant="filled"
              >
                {isEvaluating ? 'Re-analyzing Risk...' : 'Re-analyze Risk'}
              </Button>
            </Group>
          </Stack>
        </Card>
      )}

      {/* FastTrack Results - show forms when we have AI results OR saved evaluation data */}
      {(fastTrackResult || hasEvaluationData) && (
        <Grid gutter="lg">
          {/* Left Panel - Evaluation Matrix */}
          <Grid.Col span={8}>
            <Card withBorder radius="md" p="lg" style={{ height: '600px', overflow: 'auto' }}>
            <Stack spacing={0} mb="lg">
              <Group position="apart" align="center">
                <Title order={4}>Risk Evaluation Matrix</Title>
                <Group spacing="xs">
                  <Tooltip label={Object.values(isGeneratingTheme).some(generating => generating) ? "Generating evaluations..." : "Generate evaluation for selected themes"}>
                    <ActionIcon
                      variant={Object.values(isGeneratingTheme).some(generating => generating) ? "filled" : "subtle"}
                      size="lg"
                      color={Object.values(isGeneratingTheme).some(generating => generating) ? "blue" : "gray"}
                      onClick={() => {
                        // Generate for all selected themes
                        Object.entries(themeSelections).forEach(([themeId, isSelected]) => {
                          if (isSelected && !isGeneratingTheme[themeId]) {
                            handleGenerateThemeEvaluation(themeId);
                          }
                        });
                      }}
                      disabled={Object.values(isGeneratingTheme).some(generating => generating)}
                    >
                      <AIGeneratingIcon 
                        isGenerating={Object.values(isGeneratingTheme).some(generating => generating)} 
                        size={18} 
                        color={Object.values(isGeneratingTheme).some(generating => generating) ? "white" : "gray"}
                      />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Re-analyze all themes">
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      onClick={handleFastTrackEvaluation}
                      disabled={isEvaluating}
                    >
                      <IconRefresh size={18} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label="Save risk evaluation">
                    <ActionIcon
                      variant="subtle"
                      size="lg"
                      onClick={handleSave}
                      disabled={!hasChanges}
                      color={hasChanges ? 'orange' : 'gray'}
                    >
                      <IconDeviceFloppy size={18} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Group>
              <Group spacing="xs" mt="xs">
                <IconCheck size={14} color="green" />
                <Text size="xs" color="dimmed">AI-evaluated themes ready for editing</Text>
              </Group>
            </Stack>

            {/* Evaluation Matrix Table */}
            <Table highlightOnHover withBorder>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th style={{ width: '40%' }}>Impact Area</Table.Th>
                  <Table.Th style={{ width: '25%' }}>Impact</Table.Th>
                  <Table.Th style={{ width: '25%' }}>Likelihood</Table.Th>
                  <Table.Th style={{ width: '10%' }}>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {/* Selected/Evaluated Themes */}
                {(() => {
                  // Get selected theme IDs from themeSelections (which includes both AI and saved)
                  const selectedThemeIds = Object.entries(themeSelections)
                    .filter(([id, selected]) => selected)
                    .map(([id]) => parseInt(id));

                  // Create theme objects for rendering
                  return selectedThemeIds.map(themeId => {
                    const theme = themes.find(t => t.theme_id === themeId);
                    const aiTheme = fastTrackResult?.selectedThemes?.find(t => t.themeId === themeId);
                    const savedData = hasEvaluationData ? savedRisk.evaluationData[themeId] : null;
                    
                    return {
                      themeId,
                      themeName: theme?.name || '',
                      selected: true,
                      evaluation: aiTheme?.evaluation || savedData || { reasoning: 'Generating evaluation...' },
                      highLevelAssessment: aiTheme?.highLevelAssessment || {
                        overallImpact: savedData?.riskImpact?.overallImpact || '',
                        probability: savedData?.likelihood?.probability || ''
                      }
                    };
                  });
                })().map((theme) => {
                  const themeEval = themeEvaluations[theme.themeId];
                  
                  return (
                    <React.Fragment key={theme.themeId}>
                      <Table.Tr>
                        {/* Theme Name with Checkbox and AI Reasoning */}
                        <Table.Td>
                          <Stack spacing="xs">
                            <Group spacing="sm">
                              <Checkbox
                                checked={themeSelections[theme.themeId] || false}
                                onChange={(e) => handleThemeToggle(theme.themeId, e.target.checked)}
                              />
                              <Text weight={500}>{theme.themeName}</Text>
                              {isGeneratingTheme[theme.themeId] && (
                                <AIGeneratingIcon isGenerating={true} size={14} />
                              )}
                            </Group>
                            {/* AI Reasoning and Confidence */}
                            {(theme.evaluation?.reasoning || themeEval?.reasoning || isGeneratingTheme[theme.themeId]) && (
                              <Group spacing="xs">
                                <Text size="xs" color="dimmed" truncate style={{ maxWidth: '250px' }}>
                                  {isGeneratingTheme[theme.themeId] 
                                    ? 'AI: Generating evaluation...' 
                                    : `AI: ${themeEval?.reasoning || theme.evaluation?.reasoning}`
                                  }
                                </Text>
                                {fastTrackResult?.confidence && !isGeneratingTheme[theme.themeId] && (
                                  <Badge size="xs" color="green" variant="light">
                                    {(fastTrackResult.confidence * 100).toFixed(0)}% confidence
                                  </Badge>
                                )}
                              </Group>
                            )}
                          </Stack>
                        </Table.Td>
                        
                        {/* Impact Column */}
                        <Table.Td>
                          <Group spacing="xs">
                            <Select
                              value={themeEval?.impact?.overallImpact || theme.highLevelAssessment?.overallImpact || ''}
                              onChange={(value) => handleEvaluationChange(theme.themeId, 'impact', 'overallImpact', value)}
                              data={getRiskImpactOptions()}
                              size="xs"
                              style={{ 
                                backgroundColor: getRiskColor(themeEval?.impact?.overallImpact || theme.highLevelAssessment?.overallImpact),
                                color: ['Critical', 'High'].includes(themeEval?.impact?.overallImpact || theme.highLevelAssessment?.overallImpact) ? 'white' : 'black'
                              }}
                              w={100}
                            />
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              onClick={() => toggleCellExpansion(theme.themeId, 'impact')}
                            >
                              {expandedCells[`${theme.themeId}-impact`] ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                        
                        {/* Likelihood Column */}
                        <Table.Td>
                          <Group spacing="xs">
                            <Select
                              value={themeEval?.impact?.probability || theme.highLevelAssessment?.probability || ''}
                              onChange={(value) => handleEvaluationChange(theme.themeId, 'impact', 'probability', value)}
                              data={getLikelihoodOptions()}
                              size="xs"
                              style={{ 
                                backgroundColor: getRiskColor(themeEval?.impact?.probability || theme.highLevelAssessment?.probability),
                                color: ['Almost Certain', 'Likely'].includes(themeEval?.impact?.probability || theme.highLevelAssessment?.probability) ? 'white' : 'black'
                              }}
                              w={100}
                            />
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              onClick={() => toggleCellExpansion(theme.themeId, 'likelihood')}
                            >
                              {expandedCells[`${theme.themeId}-likelihood`] ? <IconChevronUp size={12} /> : <IconChevronDown size={12} />}
                            </ActionIcon>
                          </Group>
                        </Table.Td>
                        
                        {/* Status Column */}
                        <Table.Td>
                          <Badge 
                            size="xs" 
                            color={hasChanges ? 'orange' : 'green'}
                            leftSection={hasChanges ? <IconExclamationMark size={10} /> : <IconCheck size={10} />}
                          >
                            {hasChanges ? 'Modified' : 'AI Evaluated'}
                          </Badge>
                        </Table.Td>
                      </Table.Tr>
                      
                      {/* Expanded Impact Details Row */}
                      {expandedCells[`${theme.themeId}-impact`] && (
                        <Table.Tr>
                          <Table.Td colSpan={4}>
                            <Box p="sm" style={{ backgroundColor: '#f8f9ff' }}>
                              <Text size="sm" weight={500} color="indigo" mb="sm">Impact Deep Dive</Text>
                              <Stack spacing="sm">
                                <Group grow>
                                  <Select
                                    label="Intensity"
                                    value={themeEval?.impact?.intensity || ''}
                                    onChange={(value) => handleEvaluationChange(theme.themeId, 'impact', 'intensity', value)}
                                    data={getIntensityOptions()}
                                    size="sm"
                                    clearable
                                  />
                                  <Select
                                    label="Duration" 
                                    value={themeEval?.impact?.duration || ''}
                                    onChange={(value) => handleEvaluationChange(theme.themeId, 'impact', 'duration', value)}
                                    data={getDurationOptions()}
                                    size="sm"
                                    clearable
                                  />
                                </Group>
                              </Stack>
                            </Box>
                          </Table.Td>
                        </Table.Tr>
                      )}
                      
                      {/* Expanded Likelihood Details Row */}
                      {expandedCells[`${theme.themeId}-likelihood`] && (
                        <Table.Tr>
                          <Table.Td colSpan={4}>
                            <Box p="sm" style={{ backgroundColor: '#fff8f0' }}>
                              <Text size="sm" weight={500} color="orange" mb="sm">Likelihood Deep Dive</Text>
                              <Stack spacing="sm">
                                <Select
                                  label="Frequency of Occurrence"
                                  value={themeEval?.impact?.frequencyOfOccurrence || ''}
                                  onChange={(value) => handleEvaluationChange(theme.themeId, 'impact', 'frequencyOfOccurrence', value)}
                                  data={getFrequencyOptions()}
                                  size="sm"
                                  clearable
                                />
                              </Stack>
                            </Box>
                          </Table.Td>
                        </Table.Tr>
                      )}
                    </React.Fragment>
                  );
                })}
                
                {/* Available Themes (Not Yet Selected) */}
                {themes && themes
                  .filter((theme) => {
                    // Only show themes that are NOT selected anywhere
                    const isSaved = hasEvaluationData && savedRisk.evaluationData[theme.theme_id];
                    const isUserSelected = themeSelections[theme.theme_id];
                    return !isSaved && !isUserSelected;
                  })
                  .slice(0, 3)
                  .map((theme) => (
                  <Table.Tr key={`unselected-${theme.theme_id}`} style={{ opacity: 0.7 }}>
                    <Table.Td>
                      <Group spacing="sm">
                        <Checkbox
                          checked={themeSelections[theme.theme_id] || false}
                          onChange={(e) => {
                            handleThemeToggle(theme.theme_id, e.target.checked);
                            if (e.target.checked) {
                              handleGenerateThemeEvaluation(theme.theme_id);
                            }
                          }}
                        />
                        <div>
                          <Text size="sm">{theme.name}</Text>
                          <Text size="xs" color="dimmed">{theme.description}</Text>
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {isGeneratingTheme[theme.theme_id] ? (
                        <Group spacing="xs">
                          <AIGeneratingIcon isGenerating={true} size={16} />
                          <Badge size="sm" color="blue" variant="light">Evaluating</Badge>
                        </Group>
                      ) : themeSelections[theme.theme_id] ? (
                        <Badge size="sm" color="green" variant="light">Ready</Badge>
                      ) : (
                        <Text size="xs" color="dimmed">Select to evaluate</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      {isGeneratingTheme[theme.theme_id] ? (
                        <Group spacing="xs">
                          <AIGeneratingIcon isGenerating={true} size={16} />
                          <Badge size="sm" color="blue" variant="light">Evaluating</Badge>
                        </Group>
                      ) : themeSelections[theme.theme_id] ? (
                        <Badge size="sm" color="green" variant="light">Ready</Badge>
                      ) : (
                        <Text size="xs" color="dimmed">Select to evaluate</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge size="xs" color="gray">Available</Badge>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            </Card>
          </Grid.Col>

          {/* Right Panel - Priority & Rationale */}
          <Grid.Col span={4}>
            <Card withBorder radius="md" p="lg" style={{ height: '600px' }}>
              <Stack spacing="lg" style={{ height: '100%' }}>
                <div>
                  <Text size="lg" weight={600} mb="md">🎯 Priority</Text>
                  <Select
                    label="Priority Level"
                    value={localPriority}
                    onChange={setLocalPriority}
                    data={PRIORITY_LEVELS}
                    radius="sm"
                    size="sm"
                  />
                </div>

                <Box style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Group spacing="xs" mb="md">
                    <Text size="lg" weight={600}>📝 Rationale</Text>
                    {fastTrackResult?.methodology && (
                      <Badge 
                        color={fastTrackResult.methodology === 'Quantitative' ? 'blue' : 'orange'} 
                        size="xs"
                        variant="light"
                      >
                        {fastTrackResult.methodology}
                      </Badge>
                    )}
                  </Group>
                  <Textarea
                    placeholder="AI-generated rationale (you can modify)..."
                    value={localRationale}
                    onChange={(e) => setLocalRationale(e.currentTarget.value)}
                    radius="sm"
                    size="sm"
                    style={{ flex: 1 }}
                    autosize
                    minRows={8}
                  />
                </Box>
                
                {/* Update Priority Button */}
                <Button
                  onClick={handleSave}
                  disabled={!hasChanges}
                  size="md"
                  color={hasChanges ? 'orange' : 'gray'}
                  variant={hasChanges ? 'filled' : 'light'}
                  leftSection={<AIGeneratingIcon isGenerating={isSaving} size={16} color={hasChanges ? 'white' : 'gray'} />}
                  fullWidth
                  style={{ marginTop: 'auto' }}
                >
                  {isSaving ? 'Updating Priority...' : hasChanges ? 'Update Risk Priority' : 'Priority Saved'}
                </Button>
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>
      )}


      {/* Completion Status */}
      {(fastTrackResult || hasEvaluationData) && !isComplete && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="yellow"
          variant="light"
          radius="md"
          mb="xl"
        >
          Please select at least one theme and complete the priority and rationale to continue.
        </Alert>
      )}

      {/* Navigation */}
      <Navigation
        onBack={onBack}
        onNext={onNext}
        nextLabel="Continue to Action Plan"
        nextDisabled={!isComplete}
        showBack={!!onBack}
      />
    </Container>
  );
}